import { Checkbox, Collapse, makeStyles, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import classnames from 'classnames'
import React from 'react'

/**
 * Corner radius **in pixels** of the outlined (unchecked) checkbox in every estimateNext table. The
 * 4.57 is the literal value from the design spec (ENG-1361), not a hand-picked number — keep it
 * exact rather than rounding to 4 or 5. Shared with `OutlinedItemTable`'s row checkboxes so the
 * header select-all and the row boxes stay identical.
 */
export const CHECKBOX_RADIUS = 4.57

/**
 * The **checked** and **indeterminate** glyphs for those same checkboxes. MUI's own icons round their
 * box at 2 of 24 units, so a checked box came out visibly squarer than the unchecked one beside it —
 * these redraw the identical 18-unit box (3 → 21) at {@link CHECKBOX_RADIUS} instead.
 *
 * The tick / bar is a hole rather than a painted shape (`fillRule="evenodd"`), which is how MUI draws
 * them too: the box takes `currentColor`, so the mark shows whatever sits behind it. That keeps the
 * dark navy header working, where the box is white and the tick reads as the band's own colour.
 *
 * A bare `<svg>` rather than a MUI `SvgIcon`, which this package may not import directly. `1em` sizing
 * reproduces what `SvgIcon` does, so the `& svg { fontSize: 24 }` rule on each checkbox still sizes it.
 */
const CheckboxGlyph = ({ mark }: { mark: string }) => {
  const r = CHECKBOX_RADIUS
  const box =
    `M${3 + r} 3H${21 - r}A${r} ${r} 0 0 1 21 ${3 + r}V${21 - r}` +
    `A${r} ${r} 0 0 1 ${21 - r} 21H${3 + r}A${r} ${r} 0 0 1 3 ${21 - r}V${3 + r}A${r} ${r} 0 0 1 ${3 + r} 3Z`
  return (
    <svg
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      fill="currentColor"
      focusable="false"
      aria-hidden="true"
      style={{ flexShrink: 0, userSelect: 'none' }}
    >
      <path fillRule="evenodd" d={`${box}${mark}`} />
    </svg>
  )
}

export const CheckedCheckboxIcon = () => <CheckboxGlyph mark="M10 17L5 12l1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />

export const IndeterminateCheckboxIcon = () => <CheckboxGlyph mark="M17 13H7v-2h10v2z" />

export interface OutlinedTableCardProps {
  /**
   * Tier-1 section title shown in a white strip at the top of the card (e.g. "Options", "Payments").
   * When provided, the card reads as two-tier: this white strip, then the (light) column band below.
   */
  sectionTitle?: React.ReactNode
  /** Right-aligned content in the tier-1 section strip (e.g. a Hide pill, signature, approve button). */
  sectionAction?: React.ReactNode
  /**
   * Right-aligned helper copy in the tier-1 section strip, sitting left of `sectionAction` (e.g.
   * "Select items below to add to your estimate"). Hidden below `sm` so it can't crowd the title.
   */
  sectionSubtitle?: React.ReactNode
  /**
   * Bump the section title a step for sections that need to carry more weight in the customer-facing
   * document (currently Options, which is the one section the customer is asked to act on). Opt-in per
   * card so the editor and every other section keep the standard strip title.
   */
  largeSectionTitle?: boolean
  /**
   * When defined, the tier-2 band + body + footer collapse/expand (the section strip stays visible).
   * Leave undefined for cards that never collapse (e.g. the Item table).
   */
  collapsed?: boolean

  /** Tier-2 band title (the column label, e.g. "Item"). */
  title?: React.ReactNode
  /** Right-aligned content in the tier-2 band (e.g. a currency symbol). */
  headerRight?: React.ReactNode
  /**
   * Renders a select-all checkbox at the far left of the tier-2 band. When provided, the box's
   * checked/indeterminate state and change handler come from here.
   */
  selectAll?: {
    checked: boolean
    indeterminate?: boolean
    onChange: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void
  }
  /** Card body — the rows. */
  children?: React.ReactNode
  /** Optional footer row (e.g. an add button). Rendered below the body with a top divider. */
  footer?: React.ReactNode
  /**
   * Optional detached footer (e.g. the dashed "Add …" box) rendered as a sibling *below* the inner
   * bordered block, with a gap — so it reads as its own separate box, not part of the item list.
   * Hidden while the card is collapsed.
   */
  detachedFooter?: React.ReactNode
  /** Hide the tier-2 band entirely (used when a section has no column band). */
  hideHeader?: boolean
  /**
   * Drop the tier-1 section strip's bottom divider. Use when nothing renders below the strip inside the
   * inner block — e.g. solo-header mode (`hideHeader`) with an empty row list, where the divider would
   * otherwise stack against the card's own bottom border and read as a doubled line. The `collapsed`
   * case is handled automatically.
   */
  hideSectionDivider?: boolean
  /**
   * Drop the tier-2 band's bottom divider. Use when the body below the band is collapsed/empty so the
   * band's divider doesn't stack against the card's own bottom border (reads as a doubled line). Only
   * relevant for cards that keep the band visible while collapsing their body themselves (e.g. Products).
   */
  hideHeaderDivider?: boolean
  /**
   * Tier-2 band fill: `slate` (P85), `light` (P98, default), or `dark` (P30 navy, white content —
   * the single-tier Item header). The two-tier mocks use the lighter band beneath the white strip.
   */
  headerVariant?: 'slate' | 'light' | 'dark'
  /**
   * Collapse the default "double border" to a single bordered card (drops the outer padded frame,
   * keeping the inner block as the one border). Used by the Archived section; other sections keep
   * the double frame.
   */
  singleBorder?: boolean
  /** Extra class applied to the tier-1 section strip, for a caller that needs its own padding/sizing. */
  sectionStripClassName?: string
  className?: string
}

const useStyles = makeStyles(
  (theme: Theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    return {
      // Outer "double border" frame: a rounded, lightly-bordered white card with 4px inner padding, so
      // there's a visible gap between this outer border and the inner content block below. Border /
      // radius / padding match the Figma frame spec (0.8px P85, radius 10, padding 4).
      root: {
        border: `0.8px solid ${neutral.P85}`,
        borderRadius: 10,
        backgroundColor: neutral.White,
        padding: theme.spacing(0.5),
        // Stack the inner block + any detached footer (add box) with a gap between them.
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
        '@media print': {
          border: 'none',
          padding: 0
        }
      },
      // Single-border variant: drop the outer frame so only the inner block's border shows.
      rootSingle: {
        border: 'none',
        borderRadius: 0,
        padding: 0
      },
      // Single-border variant: the inner block is the only frame, so it carries the full 10px radius.
      innerSingle: {
        borderRadius: '10px !important'
      },
      // Inner content block: its own rounded border, clipped so the (dark/slate/light) header and the
      // last row's corners round cleanly. This is the second of the "double border"; its radius nests
      // concentrically inside the outer frame (outer 10 − 4px padding = 6).
      inner: {
        border: `0.8px solid ${neutral.P85}`,
        borderRadius: 6,
        backgroundColor: neutral.White,
        overflow: 'hidden',
        '@media print': {
          border: 'none'
        }
      },
      // Tier-1: white section strip (section title + optional action), measured from the mocks
      // (~46px tall, 16px left, 12px right, faint bottom divider).
      sectionStrip: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        minHeight: 46,
        padding: theme.spacing(0, 1.5, 0, 2),
        backgroundColor: neutral.White,
        borderBottom: `1px solid ${neutral.P90}`
      },
      // When nothing renders below the strip inside the inner block (collapsed, or an empty row list in
      // solo-header mode) the strip *is* the whole card — drop its bottom divider so it doesn't stack
      // against the inner block's bottom border + outer frame (which read as a doubled/whitish line).
      sectionStripFlush: {
        borderBottom: 'none'
      },
      // "Solo header" mode: when there's no tier-2 column band, the section strip is the only header,
      // so it reads as a proper column header — a touch darker (P98) — and it hosts the select-all
      // checkbox itself.
      sectionStripSolo: {
        backgroundColor: neutral.P98
      },
      // Left group of the section strip (select-all checkbox + title), so the title/checkbox stay
      // together on the left while the action stays right (space-between).
      sectionStripLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        minWidth: 0
      },
      // 15px/600 — the same step as the tier-2 band title ("Item") and the Payments strip, so every
      // table header in the document reads at one level (ENG-1361).
      sectionTitle: {
        fontWeight: 600,
        fontSize: theme.typography.pxToRem(15),
        lineHeight: 1.2,
        color: neutral.P35
      },
      // Must stay declared after `sectionTitle` — same specificity, so source order in this sheet is
      // what makes it win.
      sectionTitleLarge: {
        fontWeight: 600,
        fontSize: theme.typography.pxToRem(16)
      },
      // Right-aligned helper copy in the strip. Deliberately quiet next to the title, and dropped on
      // small screens where it would squeeze the title/action rather than help.
      sectionSubtitle: {
        fontSize: theme.typography.pxToRem(12),
        color: neutral.P60,
        textAlign: 'right',
        minWidth: 0,
        [theme.breakpoints.down('xs')]: {
          display: 'none'
        }
      },
      // Holds the subtitle + action together on the right so `space-between` still splits the strip
      // into title | (subtitle, action).
      sectionStripRight: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        justifyContent: 'flex-end',
        minWidth: 0
      },
      sectionAction: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexShrink: 0
      },
      // Tier-2: column band with the select-all checkbox + column label.
      header: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        minHeight: 48,
        padding: theme.spacing(0, 1.5, 0, 2),
        borderBottom: `1px solid ${neutral.P90}`
      },
      // Drop the band's bottom divider when its body is collapsed/empty, so it doesn't double up
      // against the card's own bottom border.
      headerFlush: {
        borderBottom: 'none'
      },
      headerSlate: {
        backgroundColor: neutral.P85,
        minHeight: 48
      },
      headerLight: {
        backgroundColor: neutral.P98,
        // the lighter tier-2 band reads a touch shorter than the slate single-tier header
        minHeight: 40
      },
      // Dark navy single-tier header (the Item card): white checkbox / title / actions.
      headerDark: {
        backgroundColor: neutral.P35,
        minHeight: 48
      },
      checkbox: {
        padding: 0,
        // MUI's checked/indeterminate glyph draws its box *inset* in a 24-unit viewBox (spans 3→21, so
        // 18 of 24 units), which means the default 24px font size renders an 18px box — exactly matching
        // the unchecked div below. Shrinking this to 18 makes the checked state visibly smaller.
        '& svg': {
          fontSize: theme.typography.pxToRem(24)
        }
      },
      checkboxDark: {
        color: neutral.White,
        '& svg': {
          color: neutral.White
        }
      },
      // Outlined (empty) unchecked box instead of the shared Checkbox's grey fill — light border on
      // light headers, white border on the dark Item header.
      emptyCheckboxIcon: {
        backgroundColor: 'transparent !important',
        border: `1px solid ${neutral.P80}`,
        borderRadius: CHECKBOX_RADIUS,
        boxSizing: 'border-box'
      },
      emptyCheckboxIconDark: {
        backgroundColor: 'transparent !important',
        border: `1px solid ${neutral.White}`,
        borderRadius: CHECKBOX_RADIUS,
        boxSizing: 'border-box'
      },
      title: {
        fontWeight: 600,
        fontSize: theme.typography.pxToRem(15),
        lineHeight: 1.2,
        color: neutral.P35
      },
      titleDark: {
        color: neutral.White
      },
      headerRight: {
        marginLeft: 'auto',
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1)
      },
      body: {},
      footer: {
        borderTop: `1px solid ${neutral.P90}`
      }
    }
  },
  { name: 'OutlinedTableCard' }
)

/**
 * The shared "outlined" card container for the new (estimateNext) pipeline. Two-tier by design (per
 * the mocks): an optional white **section strip** (section title + action) on top, then a lighter
 * **column band** (select-all checkbox + column label + right content), then the body rows and an
 * optional footer. When `collapsed` is defined, everything below the section strip collapses.
 */
export default function OutlinedTableCard({
  sectionTitle,
  sectionAction,
  sectionSubtitle,
  largeSectionTitle,
  collapsed,
  title,
  headerRight,
  selectAll,
  children,
  footer,
  detachedFooter,
  hideHeader,
  hideSectionDivider,
  hideHeaderDivider,
  headerVariant = 'light',
  singleBorder,
  sectionStripClassName,
  className
}: OutlinedTableCardProps) {
  const classes = useStyles()
  const isDark = headerVariant === 'dark'

  const content = (
    <>
      {!hideHeader && (
        <div
          className={classnames(classes.header, {
            [classes.headerSlate]: headerVariant === 'slate',
            [classes.headerLight]: headerVariant === 'light',
            [classes.headerDark]: isDark,
            [classes.headerFlush]: hideHeaderDivider
          })}
        >
          {selectAll && (
            // `secondary` checks in white (for the dark navy band); on the light/slate bands that would
            // be all but invisible, so those use `primary` (checks in P50, like the row checkboxes).
            <Checkbox
              color={isDark ? 'secondary' : 'primary'}
              className={classnames(classes.checkbox, { [classes.checkboxDark]: isDark })}
              classes={{ uncheckedIcon: isDark ? classes.emptyCheckboxIconDark : classes.emptyCheckboxIcon }}
              checkedIcon={<CheckedCheckboxIcon />}
              indeterminateIcon={<IndeterminateCheckboxIcon />}
              checked={selectAll.checked}
              indeterminate={selectAll.indeterminate}
              onChange={selectAll.onChange}
            />
          )}
          {typeof title === 'string' ? (
            <Typography variant="subtitle1" className={classnames(classes.title, { [classes.titleDark]: isDark })}>
              {title}
            </Typography>
          ) : (
            title
          )}
          {headerRight && <div className={classes.headerRight}>{headerRight}</div>}
        </div>
      )}
      <div className={classes.body}>{children}</div>
      {footer && <div className={classes.footer}>{footer}</div>}
    </>
  )

  return (
    <div className={classnames(classes.root, { [classes.rootSingle]: singleBorder }, className)}>
      <div className={classnames(classes.inner, { [classes.innerSingle]: singleBorder })}>
        {sectionTitle != null && (
          <div
            className={classnames(
              classes.sectionStrip,
              {
                [classes.sectionStripFlush]: collapsed === true || hideSectionDivider === true,
                [classes.sectionStripSolo]: hideHeader === true
              },
              sectionStripClassName
            )}
          >
            <div className={classes.sectionStripLeft}>
              {/* With no tier-2 band, the select-all checkbox lives in the strip itself. */}
              {hideHeader && selectAll && (
                // The strip is always light, so `primary` (P50 check) — never the white `secondary`.
                <Checkbox
                  color="primary"
                  className={classes.checkbox}
                  classes={{ uncheckedIcon: classes.emptyCheckboxIcon }}
                  checkedIcon={<CheckedCheckboxIcon />}
                  indeterminateIcon={<IndeterminateCheckboxIcon />}
                  checked={selectAll.checked}
                  indeterminate={selectAll.indeterminate}
                  onChange={selectAll.onChange}
                />
              )}
              {typeof sectionTitle === 'string' ? (
                <Typography
                  variant="subtitle1"
                  className={classnames(classes.sectionTitle, {
                    [classes.sectionTitleLarge]: largeSectionTitle
                  })}
                >
                  {sectionTitle}
                </Typography>
              ) : (
                sectionTitle
              )}
            </div>
            {(sectionSubtitle != null || sectionAction) && (
              <div className={classes.sectionStripRight}>
                {typeof sectionSubtitle === 'string' ? (
                  // `component="span"` is load-bearing: the document root spreads
                  // getPresentationContentStyles, whose `& p` rule (body1 size + `color: inherit`) is
                  // a descendant selector and outranks this single class — Typography's default <p>
                  // would silently lose its size and color.
                  <Typography component="span" className={classes.sectionSubtitle}>
                    {sectionSubtitle}
                  </Typography>
                ) : (
                  sectionSubtitle
                )}
                {sectionAction && <div className={classes.sectionAction}>{sectionAction}</div>}
              </div>
            )}
          </div>
        )}
        {collapsed === undefined ? content : <Collapse show={!collapsed}>{content}</Collapse>}
      </div>
      {detachedFooter && !collapsed && detachedFooter}
    </div>
  )
}
